import {
  AgGrid,
  Button,
  ErrorMessage,
  Heading,
  IconDropdown,
  InfoPopup,
  MobileResponsiveGrid,
  Spinner,
} from "@/common/components";
import { useContext, useEffect, useSelector, useState } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  useGetRecordKeepingSystemContactQuery,
  useGetRecordKeepingSystemQuery,
  useDeleteRecordKeepingSystemContactMutation,
} from "@/service/AdminService";
import { setRecordKeepersData } from "@/store/adminSlice";
import { formatPhoneNumber, formatRoleCode } from "@/utils/Utilities";

const RecordKeepers = () => {
  const {
    setNavHeader,
    skipToken,
    Routes,
    navigate,
    labels,
    setPageHeader,
    dispatch,
    setNavCancelFunc,
  } = useContext(PrivateContext);

  const [rowDataForRK, setRowDataForRK] = useState([]);
  const [recordKeeperId, setRecordKeeperId] = useState("");
  const [contactsParam, setContactsParam] = useState(skipToken);
  const [rowDataContacts, setRowDataContacts] = useState([]);
  const [showConfirmPopup, setShowConfirmPopup] = useState("");
  const [sortOptionRK, setSortOptionRK] = useState("Name (A to Z)");
  const [sortOptionContacts, setSortOptionContacts] = useState("Role (A to Z)");
  const storeData = useSelector((state) => state?.adminReducer?.recordKeepers);

  const actionsDropdown = (props) => {
    return <IconDropdown {...props} list={props.list} />;
  };

  const columnDefsForRK = [
    {
      headerName: labels?.recordKeeper?.rkName,
      field: "recordkeeperName",
      flex: 1.5,
      sortable: true,
    },
    {
      headerName: labels?.recordKeeper?.actions,
      colId: "actions",
      cellRenderer: actionsDropdown,
      cellRendererParams: {
        list: [
          {
            text: labels?.recordKeeper?.details,
            path: Routes.recordKeeperDetails.path,
          },
        ],
      },
      sortable: false,
    },
    {
      headerName: labels?.recordKeeper?.rkTransmitterID,
      field: "id",
      flex: 1.5,
    },
    {
      headerName: labels?.recordKeeper?.rkServerHost,
      field: "serverHostName",
      flex: 1.5,
    },
    {
      headerName: labels?.recordKeeper?.rkHostPort,
      field: "hostPortNumber",
    },
    {
      headerName: labels?.recordKeeper?.rkSystem,
      field: "recordkeepingName",
      flex: 1.5,
    },
  ];

  const columnDefsContacts = [
    {
      headerName: labels?.recordKeeper?.role,
      field: "roleCode",
      cellRenderer: formatRoleCode,
      sortable: true,
    },
    {
      headerName: labels?.recordKeeper?.contactFirstName,
      field: "firstName",
    },
    {
      headerName: labels?.recordKeeper?.actions,
      colId: "actions",
      cellRenderer: actionsDropdown,
      cellRendererParams: {
        list: [
          {
            text: labels?.recordKeeper?.details,
            click: (e) => getDetailsForContact(e),
          },
          {
            text: labels?.recordKeeper?.removeContact,
            click: (e) => setShowConfirmPopup(e.id),
          },
        ],
      },
      sortable: false
    },
    {
      headerName: labels?.recordKeeper?.contactLastName,
      field: "lastName",
    },
    {
      headerName: labels?.recordKeeper?.contactPhone,
      field: "phoneNumber",
      cellRenderer: formatPhoneNumber,
    },
    {
      headerName: labels?.recordKeeper?.contactEmail,
      field: "emailAddress",
      flex: 1.5,
    },
  ];

  const {
    data: recordKeepers,
    isFetching,
    error,
  } = useGetRecordKeepingSystemQuery();

  const {
    data: contacts,
    isFetching: isFetching1,
    error: error1,
  } = useGetRecordKeepingSystemContactQuery(contactsParam);

  const [deleteContactApi, { isLoading: isFetching2, error: error2 }] =
    useDeleteRecordKeepingSystemContactMutation();

  const clearStateInStore = () => {
    dispatch(setRecordKeepersData({}));
    navigate(-1);
    return;
  };

  const onCellClickedRK = (e) => {
    if (e.colDef.colId !== "actions") {
      setRecordKeeperId(e.data.id);
    }
  };

  const getRowClassRK = (params) => {
    if (params.data.id === recordKeeperId) {
      return "hover-bg";
    }
    return "";
  };

  const onFirstDataRendered = (params) => {
    const findIdx = rowDataForRK.findIndex((e) => e.id === recordKeeperId);
    params.api.ensureIndexVisible(findIdx, "top");
  };

  const addNewRK = () => {
    navigate(Routes.recordKeeperDetails.path);
  };

  var getDetailsForContact = (params) => {
    const data = rowDataContacts.find((e) => e.id === params?.id);
    navigate(Routes.recordKeeperContactDetails.path, {
      state: {
        ...data,
        primaryExists:
          rowDataContacts.find((e) => e.roleCode === "P") &&
          data.roleCode !== "P",
      },
    });
  };

  const addNewContact = () => {
    navigate(Routes.recordKeeperContactDetails.path, {
      state: {
        recordkeeperId: recordKeeperId,
        primaryExists: rowDataContacts.find((e) => e.roleCode === "P"),
      },
    });
  };

  const removeContact = () => {
    deleteContactApi({ contactId: showConfirmPopup, recordKeeperId })
      .unwrap()
      .then((res) => {
        if (!res.errors.length) {
          // Success
        }
      });
  };

  const sortedEntitiesRK = [...rowDataForRK]?.sort((a, b) => {
    if (sortOptionRK === "Name (A to Z)") {
      return a?.recordkeeperName.localeCompare(b?.recordkeeperName);
    } else if (sortOptionRK === "Name (Z to A)") {
      return b?.recordkeeperName.localeCompare(a?.recordkeeperName);
    }
    return 0;
  });

  const sortedEntitiesContacts = [...rowDataContacts]?.sort((a, b) => {
    if (sortOptionContacts === "Role (A to Z)") {
      return a?.roleCode.localeCompare(b?.roleCode);
    } else if (sortOptionContacts === "Role (Z to A)") {
      return b?.roleCode.localeCompare(a?.roleCode);
    }
    return 0;
  });

  useEffect(() => {
    if (recordKeepers && recordKeepers?.recordkeeperRecordkeepingSystems) {
      setRowDataForRK(recordKeepers?.recordkeeperRecordkeepingSystems);
      if (!storeData?.recordKeeperId) {
        setRecordKeeperId(
          recordKeepers?.recordkeeperRecordkeepingSystems[0].id
        );
      }
    }
  }, [recordKeepers]);

  useEffect(() => {
    if (contacts && contacts?.contacts) {
      setRowDataContacts(contacts?.contacts);
    }
  }, [contacts]);

  useEffect(() => {
    if (recordKeeperId) {
      dispatch(setRecordKeepersData({ recordKeeperId: recordKeeperId }));
      setContactsParam({ recordKeeperId: recordKeeperId });
    }
  }, [recordKeeperId]);

  useEffect(() => {
    setPageHeader(labels?.recordKeeper?.title);
    setNavHeader(labels?.navbar?.admin);
    setNavCancelFunc(() => clearStateInStore);

    if (storeData?.recordKeeperId) {
      setRecordKeeperId(storeData?.recordKeeperId);
    }
  }, []);

  return (
    <section className="py-5 px-lg-5">
      {(isFetching || isFetching1 || isFetching2) && <Spinner />}
      <ErrorMessage error={error || error1 || error2} wrapperClassName="mb-2" />

      <Button
        id="addRK"
        text={labels?.recordKeeper?.addRK}
        wrapperClassName="col-md-3 col-lg-2 mb-2"
        click={addNewRK}
      />

      <AgGrid
        wrapperClassNames="mb-5 d-none d-md-block"
        printClassNames="mt-n3"
        columnDefs={columnDefsForRK}
        rowData={rowDataForRK}
        domLayout={"normal"}
        onCellClicked={onCellClickedRK}
        getRowClass={getRowClassRK}
        onFirstDataRendered={onFirstDataRendered}
      />
      <MobileResponsiveGrid
        key="recordKeepers"
        rowData={sortedEntitiesRK}
        columnDefs={columnDefsForRK}
        getRowClass={getRowClassRK}
        onCellClicked={onCellClickedRK}
        showSortDropDown={true}
        sortOption={sortOptionRK}
        setSortOption={setSortOptionRK}
        
      />

      <Heading
        text={labels?.recordKeeper?.contacts}
        wrapperClassNames="h4 border-bottom-1 pb-2 mb-3 mt-4"
        title={false}
      />

      <Button
        id="addContact"
        text={labels?.recordKeeper?.addContact}
        wrapperClassName="col-md-3 col-lg-2 mb-2"
        click={addNewContact}
      />

      <AgGrid
        wrapperClassNames="mb-3 d-none d-md-block"
        printClassNames="mt-n3"
        columnDefs={columnDefsContacts}
        rowData={rowDataContacts}
        domLayout={"normal"}
        overlayNoRowsTemplate={labels?.recordKeeper?.noContacts}
      />
      <MobileResponsiveGrid
        key="recordKeeperContacts"
        rowData={sortedEntitiesContacts}
        columnDefs={columnDefsContacts}
        noDataLabel={labels?.recordKeeper?.noContacts}
        showSortDropDown={true}
        sortOption={sortOptionContacts}
        setSortOption={setSortOptionContacts}
        
      />
      <InfoPopup
        showModal={showConfirmPopup}
        primaryButtonText={labels?.recordKeeper.ok}
        primaryButtonClick={removeContact}
        secondaryButtonText={labels?.recordKeeper.cancel}
        secondaryButtonClick={() => setShowConfirmPopup("")}
        body={labels?.recordKeeper.contactRemove}
      />
    </section>
  );
};

export default RecordKeepers;
